import { Injectable, signal } from '@angular/core';

export type DialogVariant = 'default' | 'danger';
export type DialogKind = 'confirm' | 'confirmTyped' | 'confirmProjectDelete' | 'alert' | 'prompt';

export interface DialogPromptOptions {
  title?: string;
  message: string;
  inputLabel?: string;
  inputPlaceholder?: string;
  initialValue?: string;
  confirmText?: string;
  cancelText?: string;
  required?: boolean;
  variant?: DialogVariant;
}

export interface DialogConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: DialogVariant;
}

export interface DialogConfirmTypedOptions {
  title?: string;
  message: string;
  /** User must type this value exactly (trimmed) to enable delete. */
  expectedValue: string;
  inputLabel?: string;
  inputPlaceholder?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: DialogVariant;
}

/** Jira-style delete: acknowledgment checkbox + optional typed project name. */
export interface DialogConfirmProjectDeleteOptions {
  title?: string;
  message: string;
  expectedValue: string;
  checkboxLabel: string;
  inputLabel?: string;
  inputPlaceholder?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: DialogVariant;
  /** When false, only the checkbox is required (not used for project delete in WizTasks). */
  requireTypedName?: boolean;
}

/**
 * SweetAlert-style modal dialogs (replaces window.confirm / window.alert).
 */
@Injectable({ providedIn: 'root' })
export class DialogService {
  readonly open = signal(false);
  readonly kind = signal<DialogKind>('confirm');
  readonly title = signal('');
  readonly message = signal('');
  readonly confirmText = signal('OK');
  readonly cancelText = signal('Cancel');
  readonly variant = signal<DialogVariant>('default');
  readonly expectedValue = signal('');
  readonly inputLabel = signal('');
  readonly inputPlaceholder = signal('');
  readonly typedValue = signal('');
  readonly checkboxLabel = signal('');
  readonly acknowledged = signal(false);
  readonly requireTypedName = signal(true);
  readonly promptRequired = signal(false);

  private finish: ((value?: boolean | string) => void) | null = null;
  private finishPrompt: ((value: string | null) => void) | null = null;

  /** One-button notice (replaces alert). */
  alert(message: string, title = 'Notice'): Promise<void> {
    return new Promise((resolve) => {
      this.finish = () => resolve();
      this.kind.set('alert');
      this.title.set(title);
      this.message.set(message);
      this.confirmText.set('OK');
      this.variant.set('default');
      this.open.set(true);
    });
  }

  /** Two-button confirmation (replaces confirm). Resolves true if confirmed. */
  confirm(opts: DialogConfirmOptions): Promise<boolean> {
    return new Promise((resolve) => {
      this.finish = (v) => resolve(v === true);
      this.kind.set('confirm');
      this.title.set(opts.title ?? 'Are you sure?');
      this.message.set(opts.message);
      this.confirmText.set(opts.confirmText ?? 'OK');
      this.cancelText.set(opts.cancelText ?? 'Cancel');
      this.variant.set(opts.variant ?? 'default');
      this.open.set(true);
    });
  }

  /**
   * Danger delete flow: user must retype an exact string (e.g. project name) before confirming.
   */
  confirmTypedDelete(opts: DialogConfirmTypedOptions): Promise<boolean> {
    return new Promise((resolve) => {
      this.finish = (v) => resolve(v === true);
      this.kind.set('confirmTyped');
      this.title.set(opts.title ?? 'Confirm delete');
      this.message.set(opts.message);
      this.expectedValue.set(opts.expectedValue);
      this.inputLabel.set(opts.inputLabel ?? 'Type the name to confirm');
      this.inputPlaceholder.set(opts.inputPlaceholder ?? opts.expectedValue);
      this.confirmText.set(opts.confirmText ?? 'Delete');
      this.cancelText.set(opts.cancelText ?? 'Cancel');
      this.variant.set(opts.variant ?? 'danger');
      this.requireTypedName.set(true);
      this.acknowledged.set(false);
      this.typedValue.set('');
      this.open.set(true);
    });
  }

  /** Checkbox + typed name (Jira project delete / move to trash). */
  confirmProjectDelete(opts: DialogConfirmProjectDeleteOptions): Promise<boolean> {
    return new Promise((resolve) => {
      this.finish = (v) => resolve(v === true);
      this.kind.set('confirmProjectDelete');
      this.title.set(opts.title ?? 'Confirm');
      this.message.set(opts.message);
      this.expectedValue.set(opts.expectedValue);
      this.checkboxLabel.set(opts.checkboxLabel);
      this.inputLabel.set(opts.inputLabel ?? 'Type the project name to confirm');
      this.inputPlaceholder.set(opts.inputPlaceholder ?? opts.expectedValue);
      this.confirmText.set(opts.confirmText ?? 'Confirm');
      this.cancelText.set(opts.cancelText ?? 'Cancel');
      this.variant.set(opts.variant ?? 'danger');
      this.requireTypedName.set(opts.requireTypedName !== false);
      this.acknowledged.set(false);
      this.typedValue.set('');
      this.open.set(true);
    });
  }

  /** Free-text input (replaces window.prompt). Resolves the string, or null if cancelled. */
  prompt(opts: DialogPromptOptions): Promise<string | null> {
    return new Promise((resolve) => {
      this.finishPrompt = (v) => resolve(v);
      this.kind.set('prompt');
      this.title.set(opts.title ?? 'Enter a value');
      this.message.set(opts.message);
      this.inputLabel.set(opts.inputLabel ?? '');
      this.inputPlaceholder.set(opts.inputPlaceholder ?? '');
      this.typedValue.set(opts.initialValue ?? '');
      this.confirmText.set(opts.confirmText ?? 'OK');
      this.cancelText.set(opts.cancelText ?? 'Cancel');
      this.promptRequired.set(opts.required === true);
      this.variant.set(opts.variant ?? 'default');
      this.open.set(true);
    });
  }

  promptReady(): boolean {
    if (!this.promptRequired()) return true;
    return this.typedValue().trim().length > 0;
  }

  typedValueMatches(): boolean {
    const expected = this.expectedValue().trim();
    const typed = this.typedValue().trim();
    return expected.length > 0 && typed === expected;
  }

  projectDeleteReady(): boolean {
    if (!this.acknowledged()) return false;
    if (this.requireTypedName()) return this.typedValueMatches();
    return true;
  }

  setAcknowledged(value: boolean): void {
    this.acknowledged.set(value);
  }

  setTypedValue(value: string): void {
    this.typedValue.set(value);
  }

  confirmClick(): void {
    if (this.kind() === 'confirmTyped' && !this.typedValueMatches()) {
      return;
    }
    if (this.kind() === 'confirmProjectDelete' && !this.projectDeleteReady()) {
      return;
    }
    if (this.kind() === 'prompt') {
      if (!this.promptReady()) return;
      this.finishPrompt?.(this.typedValue().trim());
      this.reset();
      return;
    }
    if (this.kind() === 'alert') {
      this.finish?.();
    } else {
      this.finish?.(true);
    }
    this.reset();
  }

  cancelClick(): void {
    if (this.kind() === 'prompt') {
      this.finishPrompt?.(null);
    } else if (this.kind() === 'confirm' || this.kind() === 'confirmTyped' || this.kind() === 'confirmProjectDelete') {
      this.finish?.(false);
    }
    this.reset();
  }

  private isCancellable(): boolean {
    const k = this.kind();
    return k === 'confirm' || k === 'confirmTyped' || k === 'confirmProjectDelete' || k === 'prompt';
  }

  /** Backdrop click: cancel confirm, dismiss alert. */
  backdropClick(): void {
    if (this.isCancellable()) {
      this.cancelClick();
    } else {
      this.confirmClick();
    }
  }

  escape(): void {
    if (this.isCancellable()) {
      this.cancelClick();
    } else {
      this.confirmClick();
    }
  }

  private reset(): void {
    this.open.set(false);
    this.typedValue.set('');
    this.expectedValue.set('');
    this.acknowledged.set(false);
    this.checkboxLabel.set('');
    this.promptRequired.set(false);
    this.finish = null;
    this.finishPrompt = null;
  }
}
